/**
 * Penalty mode — ball physics and level-object interaction (walls, wind, lasers, points, goal).
 */
import {
    circleRectOverlap,
    distPointToSeg,
    distSegToSeg2D,
    movingLaserSegNorm,
    normPointInWindZone,
    speedrunLaserHalfPxForMoving,
    speedrunLaserHalfPxForWall,
    speedrunPointGoalPosNorm,
    speedrunWindDirUnit,
    speedrunWindStrengthValue,
    sweptCircleHitsCircle,
    wallSegToPx,
    type MovingLaserDef,
    type SpeedrunGoalDef,
    type SpeedrunPointDef,
    type SpeedrunWindDef,
    type WallSegDef,
} from "./speedrun-levels";

export type PenaltyBallState = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
};

export type PenaltyGeomCtx = {
    w: number;
    h: number;
    ls: number;
    tSec: number;
    wallSegDefs: WallSegDef[];
    disabledWallIndices: Set<number>;
    movingDefs: MovingLaserDef[];
    disabledMovingIndices: Set<number>;
    pointsNorm: SpeedrunPointDef[];
    pointsCollected: Set<number>;
    windDefs: SpeedrunWindDef[];
    goal: SpeedrunGoalDef;
    goalPx: { x: number; y: number; half: number };
};

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

export function penaltyBallRadiusPx(layoutScale = 1): number {
    return Math.max(10, 14 * layoutScale);
}

/** True while the ball still has meaningful motion (strikes are disabled). */
export function penaltyBallIsMoving(ball: Pick<PenaltyBallState, "vx" | "vy">, minSpeed = 8): boolean {
    return Math.hypot(ball.vx, ball.vy) > minSpeed;
}

export function resetPenaltyBallAtNorm(
    ball: PenaltyBallState,
    nx: number,
    ny: number,
    w: number,
    h: number,
    ls: number,
) {
    ball.x = clamp(nx, 0, 1) * w;
    ball.y = clamp(ny, 0, 1) * h;
    ball.vx = 0;
    ball.vy = 0;
    ball.r = penaltyBallRadiusPx(ls);
}

/** Apply cursor strike impulse from pointer motion this frame. Returns true if hit registered. */
export function penaltyTryCursorHitBall(
    ball: PenaltyBallState,
    cx: number,
    cy: number,
    pCx: number,
    pCy: number,
    cursorR: number,
    hitCooldownMs: number,
    nowMs: number,
    lastHitMs: { v: number },
): boolean {
    if (penaltyBallIsMoving(ball)) return false;
    if (nowMs - lastHitMs.v < hitCooldownMs) return false;
    const cdx = cx - pCx;
    const cdy = cy - pCy;
    const cursorSpeed = Math.hypot(cdx, cdy);
    if (cursorSpeed < 0.35) return false;
    const sumR = ball.r + cursorR;
    const endDist = Math.hypot(ball.x - cx, ball.y - cy);
    const touching =
        endDist <= sumR + 1e-3 ||
        sweptCircleHitsCircle(pCx, pCy, cx, cy, cursorR, ball.x, ball.y, ball.r);
    if (!touching) return false;
    const nx = cdx / cursorSpeed;
    const ny = cdy / cursorSpeed;
    const impulse = Math.min(920, 180 + cursorSpeed * 4.2);
    ball.vx += nx * impulse;
    ball.vy += ny * impulse;
    lastHitMs.v = nowMs;
    return true;
}

/** Keep cursor outside the ball capsule; clip motion along the pointer segment when needed. */
export function resolvePenaltyCursorAgainstBall(
    cx: number,
    cy: number,
    pCx: number,
    pCy: number,
    ball: PenaltyBallState,
    cursorR: number,
): { x: number; y: number } {
    const sumR = ball.r + cursorR;
    const pushOut = (x: number, y: number): { x: number; y: number } => {
        let dx = x - ball.x;
        let dy = y - ball.y;
        let dist = Math.hypot(dx, dy);
        if (dist >= sumR) return { x, y };
        if (dist < 1e-6) {
            const mx = x - pCx;
            const my = y - pCy;
            if (Math.hypot(mx, my) > 1e-4) {
                dx = -mx;
                dy = -my;
            } else {
                dx = 0;
                dy = -1;
            }
            dist = Math.hypot(dx, dy) || 1;
        }
        return { x: ball.x + (dx / dist) * sumR, y: ball.y + (dy / dist) * sumR };
    };

    const mx = cx - pCx;
    const my = cy - pCy;
    const moveLen = Math.hypot(mx, my);
    if (moveLen < 1e-4) return pushOut(cx, cy);
    if (!sweptCircleHitsCircle(pCx, pCy, cx, cy, cursorR, ball.x, ball.y, ball.r)) {
        return { x: cx, y: cy };
    }

    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) * 0.5;
        const tx = pCx + mx * mid;
        const ty = pCy + my * mid;
        if (sweptCircleHitsCircle(pCx, pCy, tx, ty, cursorR, ball.x, ball.y, ball.r)) hi = mid;
        else lo = mid;
    }
    return pushOut(pCx + mx * lo, pCy + my * lo);
}

function penaltyPointHitRadiusPx(
    pts: SpeedrunPointDef[],
    i: number,
    ls: number,
    ballR: number,
): number {
    const base = Math.max(14 * ls, ballR * 1.25 + 10 * ls);
    const mul = pts[i]?.radiusMul;
    if (mul != null && Number.isFinite(mul)) return Math.max(8 * ls, Math.min(72 * ls, base * mul));
    return base;
}

function ballHitsLaserSegSweep(
    ball: PenaltyBallState,
    px0: number,
    py0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    half: number,
): boolean {
    const minClear = ball.r + half;
    const dMove = Math.hypot(ball.x - px0, ball.y - py0);
    if (dMove < 1e-4) {
        return distPointToSeg(ball.x, ball.y, x1, y1, x2, y2) < minClear;
    }
    return distSegToSeg2D(px0, py0, ball.x, ball.y, x1, y1, x2, y2) < minClear;
}

export type PenaltyBallStepResult = {
    laserHit: boolean;
    goalReached: boolean;
    pointCollected: boolean;
};

export function stepPenaltyBall(
    ball: PenaltyBallState,
    ctx: PenaltyGeomCtx,
    dt: number,
    prevX: number,
    prevY: number,
): PenaltyBallStepResult {
    const out: PenaltyBallStepResult = { laserHit: false, goalReached: false, pointCollected: false };
    const px0 = prevX;
    const py0 = prevY;

    let wx = 0;
    let wy = 0;
    const bnx = ball.x / ctx.w;
    const bny = ball.y / ctx.h;
    for (const z of ctx.windDefs) {
        if (!normPointInWindZone(bnx, bny, z)) continue;
        const u = speedrunWindDirUnit(z.dir);
        const s = speedrunWindStrengthValue(z);
        wx += u.x * s;
        wy += u.y * s;
    }
    const wlen = Math.hypot(wx, wy);
    if (wlen > 1e-6) {
        const push = 420 * dt * wlen;
        ball.vx += (wx / wlen) * push;
        ball.vy += (wy / wlen) * push;
    }

    ball.vx *= Math.pow(0.992, dt * 60);
    ball.vy *= Math.pow(0.992, dt * 60);

    ball.x += ball.vx * dt;
    ball.y += ball.vy * dt;

    ball.x = clamp(ball.x, ball.r, ctx.w - ball.r);
    ball.y = clamp(ball.y, ball.r, ctx.h - ball.r);
    if (ball.x <= ball.r || ball.x >= ctx.w - ball.r) ball.vx *= -0.72;
    if (ball.y <= ball.r || ball.y >= ctx.h - ball.r) ball.vy *= -0.72;

    for (let wi = 0; wi < ctx.wallSegDefs.length; wi++) {
        if (ctx.disabledWallIndices.has(wi)) continue;
        const sdef = ctx.wallSegDefs[wi];
        if (sdef.solidWall) continue;
        const seg = wallSegToPx(sdef, ctx.tSec, ctx.w, ctx.h);
        const half = speedrunLaserHalfPxForWall(sdef, ctx.ls);
        if (ballHitsLaserSegSweep(ball, px0, py0, seg.x1, seg.y1, seg.x2, seg.y2, half)) {
            out.laserHit = true;
            return out;
        }
    }

    for (let mi = 0; mi < ctx.movingDefs.length; mi++) {
        if (ctx.disabledMovingIndices.has(mi)) continue;
        const mv = ctx.movingDefs[mi];
        const seg = movingLaserSegNorm(mv, ctx.tSec);
        const x1 = seg.ax * ctx.w;
        const y1 = seg.ay * ctx.h;
        const x2 = seg.bx * ctx.w;
        const y2 = seg.by * ctx.h;
        const half = speedrunLaserHalfPxForMoving(mv, ctx.ls);
        if (ballHitsLaserSegSweep(ball, px0, py0, x1, y1, x2, y2, half)) {
            out.laserHit = true;
            return out;
        }
    }

    for (let i = 0; i < ctx.pointsNorm.length; i++) {
        if (ctx.pointsCollected.has(i)) continue;
        const pt = ctx.pointsNorm[i];
        const pos = speedrunPointGoalPosNorm(pt.nx, pt.ny, pt.pathMove, pt.move, ctx.tSec);
        const px = pos.nx * ctx.w;
        const py = pos.ny * ctx.h;
        const pr = penaltyPointHitRadiusPx(ctx.pointsNorm, i, ctx.ls, ball.r);
        if (Math.hypot(ball.x - px, ball.y - py) < ball.r + pr * 0.55) {
            ctx.pointsCollected.add(i);
            out.pointCollected = true;
        }
    }

    const g = ctx.goalPx;
    const side = g.half * 2;
    if (
        circleRectOverlap(ball.x, ball.y, ball.r, g.x - g.half, g.y - g.half, side, side) &&
        penaltyYellowPointsUncollected(ctx) === 0
    ) {
        out.goalReached = true;
    }

    return out;
}

export function penaltyYellowPointsUncollected(ctx: Pick<PenaltyGeomCtx, "pointsNorm" | "pointsCollected">): number {
    let n = 0;
    for (let i = 0; i < ctx.pointsNorm.length; i++) {
        if (ctx.pointsNorm[i].group) continue;
        if (!ctx.pointsCollected.has(i)) n++;
    }
    return n;
}

export function drawPenaltyBall(ctx: CanvasRenderingContext2D, ball: PenaltyBallState) {
    ctx.save();
    ctx.fillStyle = "#ffcc44";
    ctx.shadowColor = "#ffaa22";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "#fff5cc";
    ctx.lineWidth = Math.max(1.5, ball.r * 0.12);
    ctx.stroke();
    ctx.restore();
}
